import { useEffect, useState, type JSX } from 'react';
import type { TFunction } from 'i18next';
import { useInfiniteQuery } from '@tanstack/react-query';
import { auditPageSchema, type AuditEntry } from '@netdeck/contracts';
import { apiRequest } from '../../shared/api/client';
import { currentLanguage, useTranslation } from '../../shared/i18n';
import { dateTimeFormat } from '../../shared/i18n/format';
import { useErrorText } from '../../shared/i18n/useErrorText';
import { Button } from '../../shared/ui/Button';
import { DataTable, type Column } from '../../shared/ui/DataTable';
import { Icon } from '../../shared/ui/Icon';
import { EmptyState, ErrorState, Loading, Panel } from '../../shared/ui/Panel';

/**
 * Названия действий — ключами словаря. Неизвестный код показывается как есть:
 * `api` пишет в журнал и те действия, о которых интерфейс ещё не знает,
 * и подменять их прочерком значило бы прятать запись целиком.
 */
const ACTION_LABEL_KEYS: Record<string, string> = {
  'auth.login': 'audit.action.login',
  'auth.login_failed': 'audit.action.loginFailed',
  'auth.logout': 'audit.action.logout',
  'auth.refresh_reuse': 'audit.action.refreshReuse',
  'auth.password_changed': 'audit.action.passwordChanged',
  'user.invited': 'audit.action.userInvited',
  'user.updated': 'audit.action.userUpdated',
  'user.deactivated': 'audit.action.userDeactivated',
  'user.roles_changed': 'audit.action.rolesChanged',
  'org.updated': 'audit.action.orgUpdated',
  'site.created': 'audit.action.siteCreated',
  'site.updated': 'audit.action.siteUpdated',
  'site.deleted': 'audit.action.siteDeleted',
};

/** Действия, которые стоит подсветить как тревожные. */
const ALARMING = new Set(['auth.login_failed', 'auth.refresh_reuse', 'user.deactivated']);

export function AuditPage(): JSX.Element {
  const { t } = useTranslation();
  const errorText = useErrorText();
  const language = currentLanguage();

  const [action, setAction] = useState('');
  const [search, setSearch] = useState('');
  const [query_, setQuery] = useState('');

  /**
   * Запись, подробности которой открыли целиком.
   *
   * Подробности — единственное поле журнала без предсказуемой длины: у входа
   * это пара слов, у изменения прав — список ролей, у файловой операции —
   * полный путь с размером. Столбец под самую длинную из них съедал бы
   * половину таблицы ради строки, которую всё равно нельзя прочесть целиком,
   * поэтому в строке видно начало, а остальное открывается по щелчку.
   */
  const [opened, setOpened] = useState<AuditEntry | null>(null);

  /*
    Запрос уходит не на каждую букву: поиск идёт перебором по журналу
    (индекса под «содержит» нет), и запускать его на каждом нажатии значило бы
    класть базу набором слова. Треть секунды — обычная пауза между буквами
    у того, кто печатает, и незаметная для того, кто уже допечатал.
  */
  useEffect(() => {
    const timer = setTimeout(() => setQuery(search.trim()), 350);

    return () => clearTimeout(timer);
  }, [search]);

  const query = useInfiniteQuery({
    queryKey: ['audit', action, query_],
    initialPageParam: undefined as string | undefined,
    queryFn: ({ pageParam }) => {
      const params = new URLSearchParams({ limit: '50' });
      if (action) {
        params.set('action', action);
      }
      if (query_) {
        params.set('q', query_);
      }
      if (pageParam) {
        params.set('cursor', pageParam);
      }
      return apiRequest(`/audit?${params.toString()}`, auditPageSchema);
    },
    getNextPageParam: (last) => last.nextCursor ?? undefined,
  });

  const entries = query.data?.pages.flatMap((page) => page.items) ?? [];
  /*
    Общее число берётся из первой страницы: оно приходит с каждой, но именно
    первая отвечает фильтру целиком, а не остатку после курсора.
  */
  const total = query.data?.pages[0]?.total ?? null;

  const columns: Column<AuditEntry>[] = [
    {
      key: 'at',
      header: t('audit.column.at'),
      width: '160px',
      // На карточке время идёт второй строкой: узнают запись по действию.
      priority: 'summary',
      render: (entry) => (
        <span className="font-mono text-xs text-muted">{formatEntryTime(entry.at, language)}</span>
      ),
    },
    {
      key: 'action',
      header: t('audit.column.action'),
      width: '1.4fr',
      priority: 'title',
      render: (entry) => (
        <span className={ALARMING.has(entry.action) ? 'text-down-fg' : undefined}>
          {actionLabel(entry.action, t)}
        </span>
      ),
    },
    {
      key: 'user',
      header: t('audit.column.user'),
      width: '1.2fr',
      priority: 'summary',
      render: (entry) => (
        <span className="block truncate text-muted">
          {entry.userDisplayName ?? t('audit.system')}
        </span>
      ),
    },
    {
      key: 'target',
      header: t('audit.column.target'),
      width: '1fr',
      render: (entry) => (
        <span className="font-mono text-xs text-dim">{entry.targetType ?? '—'}</span>
      ),
    },
    {
      key: 'ip',
      header: t('audit.column.ip'),
      width: '140px',
      render: (entry) => <span className="font-mono text-xs text-dim">{entry.ip ?? '—'}</span>,
    },
    {
      key: 'details',
      header: t('audit.column.details'),
      width: '1fr',
      render: (entry) =>
        Object.keys(entry.details).length === 0 ? (
          <span className="font-mono text-2xs text-faint">—</span>
        ) : (
          <button
            type="button"
            onClick={() => setOpened(entry)}
            title={t('audit.showFullDetails')}
            // `block w-full` обязателен: без ширины кнопка сжимается
            // по содержимому, и обрезать многоточием становится нечего.
            className="block w-full truncate text-left font-mono text-2xs text-dim hover:text-fg"
          >
            {formatDetails(entry.details)}
          </button>
        ),
    },
  ];

  return (
    <div className="flex min-h-0 flex-1 flex-col gap-4 p-4">
      <Panel
        title={t('nav.audit')}
        subtitle={
          total === null
            ? undefined
            : // Показано и всего — потому что это разные числа: страница
              // догружается по требованию, а «всего» отвечает на вопрос,
              // ради которого в журнал и приходят.
              t('audit.subtitle', { shown: entries.length, count: total })
        }
        actions={
          <>
            <label className="relative flex flex-1 items-center sm:flex-none">
              <span className="pointer-events-none absolute left-2 text-dim">
                <Icon name="search" />
              </span>
              <input
                type="search"
                value={search}
                onChange={(event) => setSearch(event.target.value)}
                placeholder={t('audit.searchPlaceholder')}
                aria-label={t('audit.searchLabel')}
                className="h-9 w-full rounded-sm border border-line-strong bg-surface pr-2 pl-7 text-sm text-fg placeholder:text-faint sm:h-6.5 sm:w-[220px]"
              />
            </label>

            <select
              value={action}
              onChange={(event) => setAction(event.target.value)}
              className="h-9 rounded-sm border border-line-strong bg-surface px-2 text-sm text-fg sm:h-6.5"
            >
              <option value="">{t('audit.allActions')}</option>
              {Object.entries(ACTION_LABEL_KEYS).map(([code, key]) => (
                <option key={code} value={code}>
                  {t(key)}
                </option>
              ))}
            </select>
          </>
        }
      >
        {query.isPending ? (
          <Loading />
        ) : query.error ? (
          <ErrorState
            title={t('audit.loadFailed')}
            hint={t('audit.loadFailedHint', { reason: errorText(query.error) })}
          />
        ) : (
          <>
            <DataTable
              columns={columns}
              rows={entries}
              rowKey={(entry) => entry.id}
              rowDanger={(entry) => ALARMING.has(entry.action)}
              empty={
                query_ || action ? (
                  <EmptyState title={t('audit.nothingFound')} hint={t('audit.nothingFoundHint')} />
                ) : (
                  <EmptyState title={t('audit.emptyTitle')} hint={t('audit.emptyHint')} />
                )
              }
            />

            {opened ? <DetailsDialog entry={opened} onClose={() => setOpened(null)} /> : null}

            {query.hasNextPage ? (
              <div className="flex justify-center p-3">
                <Button
                  icon="chevronDown"
                  size="sm"
                  variant="ghost"
                  disabled={query.isFetchingNextPage}
                  onClick={() => void query.fetchNextPage()}
                >
                  {query.isFetchingNextPage ? t('common.loading') : t('audit.loadMore')}
                </Button>
              </div>
            ) : null}
          </>
        )}
      </Panel>
    </div>
  );
}

/**
 * Подробности записи целиком.
 *
 * Отдельным окном, а не раскрытой строкой таблицы: подробности бывают
 * в несколько строк, и раскрытая строка сдвигала бы весь журнал под собой —
 * ровно тогда, когда его читают, сравнивая соседние записи.
 *
 * Содержимое показано разобранным JSON, с отступами и переносами: журнал
 * читают, когда разбираются в происшествии, и «details={"path":"C:\\...»
 * в одну строку — это то же самое, что было в таблице, только шире.
 */
function DetailsDialog({
  entry,
  onClose,
}: {
  entry: AuditEntry;
  onClose: () => void;
}): JSX.Element {
  const { t } = useTranslation();
  const language = currentLanguage();

  const text = JSON.stringify(entry.details, null, 2);

  useEffect(() => {
    const onKey = (event: KeyboardEvent): void => {
      if (event.key === 'Escape') {
        onClose();
      }
    };

    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [onClose]);

  return (
    <div
      className="fixed inset-0 z-50 flex items-end justify-center bg-black/60 pt-[env(safe-area-inset-top)] pb-[env(safe-area-inset-bottom)] sm:items-center sm:p-4"
      // Щелчок мимо окна закрывает его — но только мимо: попадание
      // по самому окну всплывает сюда же.
      onClick={(event) => {
        if (event.target === event.currentTarget) {
          onClose();
        }
      }}
    >
      <div
        role="dialog"
        aria-modal="true"
        aria-label={t('audit.dialogLabel')}
        className="flex max-h-[92dvh] w-full max-w-[640px] flex-col rounded-t-lg border border-line bg-surface sm:max-h-[70dvh] sm:rounded-lg"
      >
        <header className="flex flex-none items-center gap-2.5 border-b border-line px-3.5 py-2.5">
          <h3 className="text-lg font-semibold">{t('audit.column.details')}</h3>
          <span className="truncate font-mono text-xs text-dim">
            {formatEntryTime(entry.at, language)} · {actionLabel(entry.action, t)} ·{' '}
            {entry.userDisplayName ?? t('audit.system')}
          </span>
          <Button
            icon="close"
            size="sm"
            variant="ghost"
            className="ml-auto"
            aria-label={t('common.action.close')}
            onClick={onClose}
          />
        </header>

        {/*
          `break-all` вместе с переносом: в подробностях лежат пути, токены
          и идентификаторы без пробелов, и без него окно распирало бы вширь
          одной длинной строкой.
        */}
        <pre className="min-h-0 flex-1 overflow-auto px-3.5 py-3 font-mono text-xs break-all whitespace-pre-wrap text-muted">
          {text}
        </pre>

        <footer className="flex flex-none items-center gap-2 border-t border-line px-3.5 py-2.5">
          {/*
            Скопировать целиком — то, ради чего подробности чаще всего
            и открывают: их несут в заявку или в переписку, а выделять мышью
            текст с переносами значит принести его половину.
          */}
          <Button icon="copy" size="sm" onClick={() => void navigator.clipboard?.writeText(text)}>
            {t('common.action.copy')}
          </Button>
          <span className="font-mono text-2xs text-faint">{t('audit.escapeHint')}</span>
        </footer>
      </div>
    </div>
  );
}

/** Подпись действия. Неизвестный код журнала показывается как есть. */
function actionLabel(action: string, t: TFunction): string {
  const key = ACTION_LABEL_KEYS[action];

  return key === undefined ? action : t(key);
}

/**
 * Время записи журнала: дата и время с секундами.
 *
 * Секунды здесь не роскошь: в журнале разбираются по порядку событий,
 * а вход и следующая за ним смена ролей укладываются в одну минуту.
 */
function formatEntryTime(iso: string, language: string): string {
  return dateTimeFormat(language, {
    hour12: false,
    day: '2-digit',
    month: '2-digit',
    year: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
    second: '2-digit',
  }).format(new Date(iso));
}

/**
 * Подробности записи приходят как произвольный JSON: их состав задаёт тот,
 * кто записал событие. Поэтому значение любого типа приводится к строке
 * через JSON.stringify, а не через String — иначе объект превратился бы
 * в бесполезное «[object Object]».
 */
function formatDetails(details: Record<string, unknown>): string {
  const entries = Object.entries(details);
  if (entries.length === 0) {
    return '—';
  }

  return entries
    .map(([key, value]) => `${key}=${typeof value === 'string' ? value : JSON.stringify(value)}`)
    .join(' ');
}
